import { useUser } from '@clerk/clerk-react';
import { DailyParticipant, DailyCall } from '@daily-co/daily-js';
import { memo, useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { ModelLoader } from '@/components/ui/ModelLoader';
import { ErrorState } from '@/components/ui/ErrorState';
import { useSessionStore } from '@/store/sessionStore';
import { useEmotionEngine } from '@/hooks/useEmotionEngine';
import { useEngagementEmitter } from '@/hooks/useEngagementEmitter';
import { initDailyCall, destroyDailyCall } from '@/lib/daily';
import { VideoGrid } from '@/components/video/VideoGrid';
import { SessionControls } from '@/components/video/SessionControls';
import { getSocket } from '@/lib/socket';
import type { EmotionProbabilities } from '@/types/emotion';



// Memoize to prevent flicker
const StableVideoGrid = memo(VideoGrid);

export const StudentSession = () => {
  const { sessionId } = useParams();
  const location = useLocation();
  const { user } = useUser();
  const [participants, setParticipants] = useState<DailyParticipant[]>([]);
  const [roomUrl] = useState<string | null>(location.state?.roomUrl ?? null);
  const [callError, setCallError] = useState<string | null>(null);

  // NEW: Default to false to reduce cognitive load
  const [showDebugOverlay, setShowDebugOverlay] = useState(false);
  
  // Media States
  const [webcamDenied, setWebcamDenied] = useState(false);
  const [videoStreamReady, setVideoStreamReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const navigate = useNavigate();
  
  // 🛡️ CRASH GUARD: Prevents duplicate calls
  const callRef = useRef<DailyCall | null>(null);
  
  // Class Over State
  const [isClassOver, setIsClassOver] = useState(false);
  const [averageEngagement, setAverageEngagement] = useState<number | null>(null);
  
  const { setSession } = useSessionStore();

  const { publish } = useEngagementEmitter({
    sessionId: sessionId ?? '',
    studentId: user?.id ?? 'anonymous',
    role: 'student',
    enabled: Boolean(sessionId)
  });

  const handlePrediction = useCallback((probabilities: EmotionProbabilities) => {
    const engaged = (probabilities.happy || 0) + (probabilities.surprise || 0);
    const confused = (probabilities.fear || 0) + (probabilities.disgust || 0);
    const disengaged = (probabilities.sad || 0) + (probabilities.angry || 0);
    
    // We now pass the Student Name as the second argument
    publish({
      ...probabilities,
      engaged,
      confused,
      disengaged,
      neutral: probabilities.neutral || 0,
      timestamp: new Date().toISOString()
    }, user?.fullName || user?.firstName || "Anonymous"); 

  }, [publish, user]); 
  
  // ✅ RESTORED: Original hook usage with videoRef (High Accuracy)
  const { isReady, progress, error, debugEmotions } = useEmotionEngine({
    videoRef,
    onPrediction: handlePrediction
  });

  const getDominantEmotion = () => {
    if (!debugEmotions) return 'Scanning...';
    const entries = Object.entries(debugEmotions);
    entries.sort((a, b) => b[1] - a[1]);
    const [name, score] = entries[0];
    return `${name.toUpperCase()} (${Math.round(score * 100)}%)`;
  };

  // 0. Listen for session_ended event AND Handle Socket Lifecycle
  useEffect(() => {
    if (!sessionId) return;

    const socket = getSocket();
    if (!socket.connected) {
      socket.connect();
    }
    socket.emit('session:join', { sessionId });

    const handleSessionEnded = (data: { averageEngagement: number }) => {
      console.log('📢 Session ended event received:', data);
      setIsClassOver(true);
      setAverageEngagement(data.averageEngagement);

      // Scorched Earth cleanup - kill camera/mic immediately
      const callObject = callRef.current;
      if (callObject) {
        callObject.leave().catch(() => {});
        callObject.destroy().catch(() => {});
      }
      destroyDailyCall();

      // Stop video stream
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
    };

    socket.on('session_ended', handleSessionEnded);

    return () => {
      socket.off('session_ended', handleSessionEnded);
      
      // ✅ FIX: Explicitly disconnect on unmount.
      // This sends the "I left" signal instantly, turning the heatmap gray immediately.
      console.log("🔌 Student leaving session: Disconnecting socket.");
      socket.disconnect(); 
    };
  }, [sessionId]);

  // 1. Daily.co Connection (WITH CRASH GUARD)
  useEffect(() => {
    if (!sessionId || !roomUrl || isClassOver) return;
    setSession({ sessionId, roomUrl, role: 'student' });

    // 🛑 STOPPER: This is the ONLY change we keep to prevent the crash
    if (callRef.current) return;

    const joinDaily = async () => {
      try {
        const call = await initDailyCall(roomUrl);
        callRef.current = call; // Mark as active

        await call.join();
        
        const updateParticipants = () => setParticipants(Object.values(call.participants() ?? {}));
        call.on('participant-joined', updateParticipants);
        call.on('participant-left', updateParticipants);
        updateParticipants();
      } catch (err) {
        console.error(err);
        // Ignore duplicate errors to be safe
        if (!(err as Error).message?.includes('Duplicate')) {
            setCallError('Unable to join Daily room.');
        }
      }
    };
    joinDaily();

    return () => {
      const callObject = callRef.current;
      if (callObject) {
        // Kill the call immediately when navigating away
        callObject.leave().catch(() => {}); // Ignore errors if already left
        callObject.destroy().catch(() => {}); // Ignore errors if already destroyed
      }
      destroyDailyCall(); // Also clean up any global instance
      callRef.current = null;
    };
  }, [roomUrl, sessionId, setSession, isClassOver]);

  // 2. RESTORED: Separate High-Res Camera Feed for AI
  useEffect(() => {
    const enableMedia = async () => {
      try {
        // High quality raw feed for BlazeFace
        const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { width: 640, height: 480, frameRate: 30 }, // Explicit quality
            audio: false // AI doesn't need audio
        });
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          setVideoStreamReady(true);
        }
      } catch (err) {
        console.warn("AI Camera Error:", err);
        setWebcamDenied(true);
      }
    };
    enableMedia();
    
    // Cleanup local stream on unmount
    return () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject as MediaStream;
            stream.getTracks().forEach(track => track.stop());
        }
    };
  }, []);

  if (!sessionId || !roomUrl) {
    return <ErrorState message="Missing session or room information. Return to lobby." />;
  }

  // Show "Class Dismissed" UI if session ended
  if (isClassOver) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="rounded-2xl border border-background-border bg-background-surface p-8 max-w-md w-full text-center shadow-glow">
          <div className="mb-6">
            <div className="text-6xl mb-4">🎓</div>
            <h2 className="text-2xl font-semibold text-text-primary mb-2">Class Dismissed</h2>
            {averageEngagement !== null && (
              <p className="text-sm text-text-dimmed">
                Average Engagement: {averageEngagement}%
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/lobby')}
            className="w-full rounded-lg bg-neon-gradient px-6 py-3 font-semibold text-white hover:shadow-glow transition-all"
          >
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="rounded-2xl border border-background-border bg-background-surface p-4 lg:col-span-3">
          <p className="text-sm text-text-dimmed">Live classroom</p>
          {callError ? (
            <ErrorState message={callError} /> 
          ) : (
            <>
                <StableVideoGrid participants={participants} />
                <div className="mt-4 border-t border-background-border pt-4">
                    <SessionControls />
                </div>
            </>
          )}
        </div>
        
        <div className="space-y-4 rounded-2xl border border-background-border bg-background-surface p-4 lg:col-span-2 relative">
          <p className="text-sm text-text-dimmed">Emotion engine</p>
          
          {!videoStreamReady && (
            <p className="text-xs text-neon-red">
              {webcamDenied ? 'Webcam unavailable.' : 'Waiting for webcam...'}
            </p>
          )}

<div className="relative">
            {/* AI Video Element (Raw High-Res Feed) */}
            <video 
              ref={videoRef} 
              autoPlay 
              muted 
              playsInline 
              className="mt-4 aspect-video w-full rounded-lg bg-black object-cover" 
            />

            {/* 1. TEXT OVERLAY: Now conditional on checkbox state */}
            {isReady && showDebugOverlay && (
              <div className="absolute top-6 left-2 bg-black/60 px-3 py-1 rounded text-text-primary font-mono text-sm border border-text-primary/20 backdrop-blur-sm z-20 animate-in fade-in duration-200">
                AI Output: {getDominantEmotion()}
              </div>
            )}
            
            {/* 2. STATUS INDICATOR: Always visible so they know it's working */}
            {isReady && (
              <div className="absolute top-6 right-2 bg-green-500/20 border border-green-500/50 text-green-400 text-xs px-2 py-1 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.4)]">
                ● AI Active
              </div>
            )}
          </div>

          {!isReady && (
            <ModelLoader progress={progress} error={error ?? undefined} />
          )}

          {/* NEW: Toggle Control */}
          <div className="flex items-center justify-between pt-2">
            <label className="flex items-center gap-2 cursor-pointer group">
              <div className="relative flex items-center">
                <input 
                  type="checkbox" 
                  className="peer sr-only" 
                  checked={showDebugOverlay}
                  onChange={(e) => setShowDebugOverlay(e.target.checked)}
                />
                <div className="w-9 h-5 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand"></div>
              </div>
              <span className="text-xs text-text-dimmed group-hover:text-text-primary transition-colors">
                Show prediction overlay
              </span>
            </label>
            
            {/* Optional: Privacy reassurance */}
            <span className="text-[10px] text-text-dimmed/60 uppercase tracking-wider">
              Processed Locally
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};